import React, { useEffect, useRef } from "react";
import { animate, spring } from "animejs";

/**
 * ModalOverlay — the one accessible modal shell.
 *
 * Gives every modal what the raw overlay divs never had: role="dialog" +
 * aria-modal, Escape to close, a focus trap, focus restoration to the
 * opener, body scroll lock, and a spring scale-in entrance (skipped under
 * prefers-reduced-motion).
 *
 * Props:
 *   onClose      – close request (backdrop click, Escape). Run any
 *                  dirty-state confirm inside this callback; returning
 *                  nothing/undefined always closes — the guard lives with
 *                  the caller's state.
 *   labelledBy   – id of the element naming the dialog (aria-labelledby)
 *   label        – fallback aria-label when no heading exists
 *   zIndex       – overlay stacking (default 999)
 *   overlayStyle / cardStyle – merged onto the shell's defaults
 *   cardClassName – extra class for the card (default adds kk-modal-card)
 */

const FOCUSABLE = 'a[href], button:not([disabled]), input, select, textarea, [tabindex]:not([tabindex="-1"])';

const reducedMotion = () =>
  window.matchMedia("(prefers-reduced-motion: reduce)").matches;

function ModalOverlay({
  onClose,
  labelledBy,
  label,
  zIndex = 999,
  overlayStyle,
  cardStyle,
  cardClassName = "",
  children,
}) {
  const cardRef = useRef(null);
  const openerRef = useRef(null);
  // The keydown listener binds once on mount; onClose must be read through a
  // ref so Escape sees the CURRENT close handler (with current dirty state),
  // not a stale first-render closure that would skip the discard confirm.
  const onCloseRef = useRef(onClose);
  onCloseRef.current = onClose;

  useEffect(() => {
    openerRef.current = document.activeElement;
    const card = cardRef.current;

    // Body scroll lock
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";

    // Move focus into the dialog
    const first = card.querySelector(FOCUSABLE);
    (first || card).focus();

    // Spring entrance — motion marks the moment, reduced-motion skips it.
    // Clear inline styles directly on completion: utils.set with "" coerces
    // opacity to 0 and would make the whole dialog vanish after the spring.
    if (!reducedMotion()) {
      animate(card, {
        opacity: [0, 1],
        scale: [0.92, 1],
        ease: spring({ stiffness: 210, damping: 18 }),
        onComplete: () => {
          card.style.opacity = "";
          card.style.transform = "";
        },
      });
    }

    const onKeyDown = (e) => {
      if (e.key === "Escape") {
        e.stopPropagation();
        onCloseRef.current?.();
        return;
      }
      if (e.key !== "Tab") return;
      // Focus trap: cycle within the dialog
      const focusables = Array.from(card.querySelectorAll(FOCUSABLE))
        .filter((el) => el.offsetParent !== null || el === document.activeElement);
      if (!focusables.length) return;
      const firstEl = focusables[0];
      const lastEl = focusables[focusables.length - 1];
      if (e.shiftKey && document.activeElement === firstEl) {
        e.preventDefault();
        lastEl.focus();
      } else if (!e.shiftKey && document.activeElement === lastEl) {
        e.preventDefault();
        firstEl.focus();
      }
    };
    document.addEventListener("keydown", onKeyDown, true);

    return () => {
      document.removeEventListener("keydown", onKeyDown, true);
      document.body.style.overflow = prevOverflow;
      openerRef.current?.focus?.();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <div
      className="kk-modal-overlay"
      onClick={() => onClose?.()}
      style={{
        position: "fixed", inset: 0, zIndex,
        background: "rgba(10,5,21,0.85)",
        backdropFilter: "blur(8px)",
        WebkitBackdropFilter: "blur(8px)",
        display: "flex", alignItems: "center", justifyContent: "center",
        padding: 16,
        ...overlayStyle,
      }}
    >
      <div
        ref={cardRef}
        role="dialog"
        aria-modal="true"
        aria-labelledby={labelledBy}
        aria-label={labelledBy ? undefined : label}
        tabIndex={-1}
        className={`kk-modal-card ${cardClassName}`.trim()}
        onClick={(e) => e.stopPropagation()}
        style={{ outline: "none", ...cardStyle }}
      >
        {children}
      </div>
    </div>
  );
}

export default ModalOverlay;
